import { VariantProps, cva } from 'class-variance-authority';
import { ChangeEvent, DetailedHTMLProps, ForwardedRef, InputHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Radio.module.scss';

const radioVariants = cva(
    [styles['radio'], 'bg-transparent', 'border border-grayscale-300', 'rounded-full', 'align-top cursor-pointer'],
    {
        variants: {
            size: {
                sm: ['w-3 h-3'],
                normal: ['w-4 h-4'],
                lg: ['w-5 h-5'],
            },
            color: {
                primary: ['checked:bg-primary checked:border-primary'],
                secondary: ['checked:bg-secondary checked:border-secondary'],
                success: ['checked:bg-success-500 checked:border-success-500'],
                danger: ['checked:bg-danger-500 checked:border-danger-500'],
                warning: ['checked:bg-warning-500 checked:border-warning-500'],
            },
            disabled: {
                true: ['bg-grayscale-100 opacity-60'],
                false: [],
            },
        },
        defaultVariants: {
            size: 'normal',
            disabled: false,
            color: 'primary',
        },
    }
);

interface RadioProps
    extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size' | 'color'>,
        VariantProps<typeof radioVariants> {
    ref?: ForwardedRef<HTMLInputElement>;
    onCheckedChange?: (checked: boolean) => void;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
    const { size, color, disabled, className, onCheckedChange, onChange, ...rest } = props;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);

        onCheckedChange?.(e.target.checked);
    };

    return (
        <input
            {...rest}
            ref={ref}
            type="radio"
            disabled={disabled}
            onChange={handleChange}
            className={twMerge(radioVariants({ size, color, disabled, className }))}
        />
    );
});

export default Radio;
